import { useState, useCallback, useEffect } from 'react'
import { api } from '~/services/api'

// Different interface for the works by worker endpoint response
interface WorkByWorker {
  workId: string
  pda: string
  proposalId: string
  proposalTitle: string
  dagId: string
  dagName: string
  worker: string // This is just a string, not an object
  title: string
  description: string
  head: string
  githubPullRequestUrl: string
  status: string
  approveVotesFor: string
  approveVotesAgainst: string
  createdAt: string
}

interface WorksByWorkerResponse {
  works: WorkByWorker[]
  totalCount: number
}

export function useWorksByWorker({ 
  proposalId, 
  walletAddress 
}: { 
  proposalId: string
  walletAddress: string 
}) {
  const [works, setWorks] = useState<WorkByWorker[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchWorks = useCallback(async () => {
    if (!proposalId || !walletAddress) {
      setWorks([])
      setTotalCount(0)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      
      const response: WorksByWorkerResponse = await api.works.getByProposalAndWorker(proposalId, walletAddress)
      
      setWorks(response.works)
      setTotalCount(response.totalCount)
    } catch (err) {
      console.error('Failed to fetch works by worker:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch works by worker'))
      setWorks([])
      setTotalCount(0)
    } finally {
      setIsLoading(false)
    }
  }, [proposalId, walletAddress])

  useEffect(() => {
    fetchWorks()
  }, [fetchWorks])

  return {
    works,
    totalCount,
    isLoading,
    error,
    refetch: fetchWorks
  }
} 